import React from 'react';
import type {RowItemProps} from '../_types';

const DEFAULT_HIT_AREA_CHECK = () => true;

/**
 * This hook provides the necessary props and behaviors for renaming our Row elements, such as RowItem and TreeRow.
 */
export const useRowRenamingProps = ({
  onClick,
  hitAreaCheck = DEFAULT_HIT_AREA_CHECK,
  onRenameOpenChange,
  shouldAllowRenaming,
  isRenaming,
}: {
  onClick: RowItemProps['onClick'];
  onRenameOpenChange: RowItemProps['onRenameOpenChange'];
  /**
   * A callback used to check if the event target is within a given hit area.
   * 
   * We recommend that you use a pattern like the following:
   * 
   * ```tsx
   * const YourComponent = () => {
   *   const hitAreaRef = React.useRef<HTMLDivElement>(null);
   * 
   *  const hitAreaCheck = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
   *   return Boolean(hitAreaRef.current?.contains(event.target as Node));
   * }, []);
   * 
   *   return (
   *     <Wrapper>
   *       <DoubleClickTarget ref={hitAreaRef} />
   *     </Wrapper>
   *   );
   * };
    
   * ```
   */
  hitAreaCheck: (event: React.MouseEvent<HTMLElement>) => boolean;
  shouldAllowRenaming: RowItemProps['shouldAllowRenaming'];
  isRenaming: RowItemProps['isRenaming'];
}) => {
  /**
   * Double click timeout.
   */
  const clickTimeout = React.useRef<NodeJS.Timeout | null>(null);
  /**
   * If we find users wanting/needing to change this, we can make it a prop.
   */
  const delay = 200;

  const clearClickTimeout = React.useCallback(() => {
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
      clickTimeout.current = null;
    }
  }, []);

  /**
   * This function handles both single and double click behavior.
   */
  const handleOnClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (isRenaming) {
        return;
      }

      /**
       * User is interacting with a keyboard.
       * In these cases, enter will always trigger renaming and space will always trigger a click.
       *
       * This replicates behavior similar to VSCode's file browser renaming.
       */
      if ('code' in event && shouldAllowRenaming) {
        switch (event.code) {
          case 'Space':
            onClick?.(event);
            break;
          case 'Enter':
            onRenameOpenChange?.(true, event, 'Enter');
        }

        clearClickTimeout();
        return;
      }

      if (
        !shouldAllowRenaming ||
        (shouldAllowRenaming && !hitAreaCheck(event))
      ) {
        /**
         * We don't need to handle double click behavior if renaming is not allowed or the user is not in the renaming hit area.
         * Instead, we just handle the regular click behavior.
         */
        onClick?.(event);

        /**
         * We want to clear our timeout in the following cases:
         *
         * - If the user clicks within a hit area and then immediately outside of it, we want to clear our timeout.
         * - If we somehow got into a state where shouldAllowRenaming changed while our click timeout was set.
         */
        clearClickTimeout();
        return;
      }

      if (clickTimeout.current !== null) {
        /**
         *  Double clicking, stop the previous click event happening.
         */
        clearClickTimeout();
        onRenameOpenChange?.(true, event, 'Click');
      } else {
        /**
         * Single Click
         */
        const timeout = setTimeout(() => {
          onClick?.(event);
          clickTimeout.current = null;
        }, delay);

        clickTimeout.current = timeout;
      }
    },
    [
      clearClickTimeout,
      delay,
      hitAreaCheck,
      isRenaming,
      onClick,
      onRenameOpenChange,
      shouldAllowRenaming,
    ]
  );

  React.useEffect(() => {
    return () => {
      clearClickTimeout();
    };
  }, [clearClickTimeout]);

  return React.useMemo(
    () => ({
      onClick: handleOnClick,
      renamingTabIndex: isRenaming ? {tabIndex: -1} : undefined,
    }),
    [handleOnClick, isRenaming]
  );
};
